import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/auth/useAuth'
import type { Money } from '@/shared/lib/money'
import { activeRule, upcomingRule, type CommissionRule } from './commissionRule'
import { fetchCommissionRules } from './commissionRuleApi'
import { profileKeys } from './profileKeys'
import {
  EMPTY_FILTER,
  fetchAgentOperations,
  fetchCommissionSummary,
  fetchFundLimits,
  fetchOutstanding,
  fetchPartnerBalance,
  fetchPartnerNode,
  type AgentOperationPage,
  type CommissionSummary,
  type FundLimits,
  type OperationFilter,
  type PartnerBalance,
  type PartnerNode,
} from './profileApi'

export interface MyAccount {
  /**
   * Sherik tugunining id'si — `GET /api/me` dagi `partyNodeId`.
   *
   * ⚠️ `null` BO'LISHI MUMKIN va bu xato emas: hisob tomon tuguniga
   * bog'lanmagan (`PartnerKind.scopes` javadoc'i shu holatni ataylab
   * ko'rib chiqadi). Unda pul ma'lumoti UMUMAN yo'q — nol emas.
   */
  agentId: string | null
  node: PartnerNode | null
  balance: PartnerBalance | null
  /**
   * Topshirilmagan naqd. `null` — so'ralmagan.
   *
   * ⚠️ `DEPOSIT` rejimida SO'RALMAYDI: o'sha rejimda `cash_in_transit`
   * ishlatilmaydi va server chaqiruvni rad etadi (§6).
   */
  outstanding: Money | null
  /**
   * ⚠️ ALOHIDA XATO va bu ataylab: `POSTPAID` da bu EKRANDAGI ASOSIY
   * RAQAM. U kelmasa «—» ko'rsatish texnik jihatdan halol, lekin sherik
   * uchun sababsiz bo'shliq bo'lardi — nega yo'qligi aytilishi kerak.
   * Qolgan sonlar esa baribir ko'rsatiladi.
   */
  outstandingError: unknown
  loading: boolean
  error: unknown
}

/**
 * Sherikning o'z hisobi.
 *
 * ⚠️ SO'ROVLAR ZANJIRLI: rejim tugundan keladi, «topshirilmagan naqd»
 * esa faqat `POSTPAID` da so'raladi. Uni har doim so'rash depozitli
 * sherikda ekranga sababsiz qizil xato chiqarardi — server
 * `SettlementModeMismatch` bilan javob beradi, chunki nol qaytarish
 * «topshirdim, ko'rinmayapti» degan nizoga olib kelardi.
 */
export function useMyAccount(): MyAccount {
  const { user } = useAuth()
  const agentId = user?.partyNodeId ?? null

  const nodeQuery = useQuery({
    queryKey: profileKeys.node(agentId ?? ''),
    queryFn: () => fetchPartnerNode(agentId ?? ''),
    enabled: agentId !== null,
    staleTime: 5 * 60_000,
  })

  const balanceQuery = useQuery({
    queryKey: profileKeys.balance(agentId ?? ''),
    queryFn: () => fetchPartnerBalance(agentId ?? ''),
    enabled: agentId !== null,
  })

  const postpaid = nodeQuery.data?.settlementMode === 'POSTPAID'
  const outstandingQuery = useQuery({
    queryKey: profileKeys.outstanding(agentId ?? ''),
    queryFn: () => fetchOutstanding(agentId ?? ''),
    enabled: agentId !== null && postpaid,
  })

  return {
    agentId,
    node: nodeQuery.data ?? null,
    balance: balanceQuery.data ?? null,
    outstanding: outstandingQuery.data ?? null,
    outstandingError: postpaid ? outstandingQuery.error : null,
    loading: agentId !== null && (nodeQuery.isPending || balanceQuery.isPending),
    error: nodeQuery.error ?? balanceQuery.error,
  }
}

export interface CommissionState {
  summary: CommissionSummary | null
  loading: boolean
  error: unknown
}

/**
 * Komissiya yig'indisi.
 *
 * @param window `null` — davrsiz so'rov, ya'ni serverning JORIY OYI
 *               (tenant zonasida). Kunlik kesim uchun `tenantDayWindow`.
 */
export function useCommissionSummary(
  agentId: string | null,
  window: { from: string; to: string } | null,
): CommissionState {
  const query = useQuery({
    queryKey: profileKeys.commission(agentId ?? '', window),
    queryFn: () => fetchCommissionSummary(agentId ?? '', window ?? undefined),
    enabled: agentId !== null,
    // ⚠️ Qayta urinish YO'Q: endpoint hali yozilmagan bo'lsa har ochilishda
    // uch marta 404 olib, ekran bir necha soniya "yuklanmoqda" da turardi.
    retry: false,
  })

  return { summary: query.data ?? null, loading: query.isPending, error: query.error }
}

export interface CommissionRuleState {
  /** HOZIR kuchda turgan qoida; `null` — QO'YILMAGAN (yoki hali yuklanmadi) */
  active: CommissionRule | null
  /** Kelajakda kuchga kiradigan eng yaqin qoida; odatda `null` */
  upcoming: CommissionRule | null
  /**
   * ⚠️ «Qoida yo'q» va «hali yuklanmadi» AJRATILADI: birinchisi
   * cashback umuman hisoblanmasligini anglatadi va u ekranda OCHIQ
   * aytilishi kerak, ikkinchisi esa vaqtinchalik bo'shliq.
   */
  loaded: boolean
  loading: boolean
  error: unknown
}

/**
 * «Men necha foizda ishlayapman» — egasi savoli (2026-08-20).
 *
 * ⚠️ SERVER BUTUN TARIXNI BERADI, ekranga esa bittasi chiqadi: ochiq VA
 * allaqachon boshlangan qoida (`activeRule`). Tarixni chiqarish kassirni
 * chalg'itardi — u «hozir qanday» degan bitta savol bilan keladi.
 *
 * ⚠️ `Date.now()` BIR MARTA olinadi va ikkala tanlovga ham beriladi:
 * ikki marta chaqirilsa qoida aynan almashayotgan onda «hozirgisi ham
 * yo'q, keyingisi ham yo'q» degan holat chiqishi mumkin edi (ADR-032 §3
 * dagi server tomondagi qaror bilan bir xil sabab).
 *
 * ⚠️ `retry: false` — {@link useCommissionSummary} bilan ayni sabab.
 */
export function useCommissionRule(agentId: string | null): CommissionRuleState {
  const query = useQuery({
    queryKey: profileKeys.commissionRule(agentId ?? ''),
    queryFn: () => fetchCommissionRules(agentId ?? ''),
    enabled: agentId !== null,
    retry: false,
    // Qoida kunlab o'zgarmaydi — kassirning telefonida har ochilishda
    // qayta so'rash sof yo'qotish (`profileKeys` boshidagi izoh).
    staleTime: 5 * 60_000,
  })

  const rules = query.data ?? null
  const now = Date.now()

  return {
    active: rules === null ? null : activeRule(rules, now),
    upcoming: rules === null ? null : upcomingRule(rules, now),
    loaded: rules !== null,
    loading: agentId !== null && query.isPending,
    error: query.error,
  }
}

export interface FundLimitsState {
  limits: FundLimits | null
  /** Chegaralar PULDA, valyuta esa faqat `/balance` da — usiz son ko'rsatilmaydi */
  currency: string | null
  loading: boolean
  error: unknown
}

/**
 * Kunlik va oylik pul chegarasi.
 *
 * ⚠️ VALYUTA HISOBDAN OLINADI: `fund-limits` javobida u YO'Q (yalang'och
 * `Map<FundLimitKind, Long>`). Taxminiy valyuta yozish sonni boshqa
 * kattalikda ko'rsatishi mumkin edi, shuning uchun balans kelmaguncha
 * chegara raqam bo'lib chizilmaydi.
 *
 * ⚠️ `retry: false` — {@link useCommissionSummary} bilan ayni sabab.
 */
export function useFundLimits(account: MyAccount): FundLimitsState {
  const agentId = account.agentId
  const query = useQuery({
    queryKey: profileKeys.fundLimits(agentId ?? ''),
    queryFn: () => fetchFundLimits(agentId ?? ''),
    enabled: agentId !== null,
    retry: false,
  })

  return {
    limits: query.data ?? null,
    currency: account.balance?.balance.currency ?? null,
    loading: agentId !== null && query.isPending,
    error: query.error,
  }
}

export interface OperationsState {
  page: AgentOperationPage | null
  loading: boolean
  error: unknown
}

/**
 * Kassa amallari ro'yxati.
 *
 * ⚠️ `retry: false` — {@link useCommissionSummary} bilan ayni sabab:
 * `404` bu yerda «doiradan tashqarida» degani ham bo'lishi mumkin va
 * uch marta qayta urinish ekranni bir necha soniya «yuklanmoqda» da
 * ushlab turardi.
 *
 * ⚠️ `moneyOnly` sukut bo'yicha `false` — «Berilgan paketlar» va
 * boshqa chaqiruvchilar ESKI xulqni oladi. Torayishni faqat «Mening
 * amallarim» oshkor so'raydi.
 */
export function useAgentOperations(
  agentId: string | null,
  window: { from: string; to: string },
  filter: OperationFilter = EMPTY_FILTER,
  page = 0,
  moneyOnly = false,
): OperationsState {
  const query = useQuery({
    queryKey: profileKeys.operations(agentId ?? '', window, filter, page, moneyOnly),
    queryFn: () => fetchAgentOperations(agentId ?? '', window, filter, page, moneyOnly),
    enabled: agentId !== null,
    retry: false,
  })

  return { page: query.data ?? null, loading: query.isPending, error: query.error }
}
